/**
 * usePainel (M10) — hooks de dados do painel/administração sobre React Query.
 *
 * - useTotais/useQualidade: polling ≤ 60s (RNF-M10 atualização do painel).
 * - useMapa(filtros): GeoJSON de pontos + distribuição por UF (RF-M10-002).
 * - useContornoUfs: contorno das 27 UFs do mapa, asset estático em chunk próprio (ADR-032).
 * - useReprocessamento: mutação POST /reprocessamentos + acompanhamento GET
 *   /reprocessamentos/{id} (polling enquanto em andamento).
 * - useConfig/useAtualizarConfig: white-label (GET/PATCH /config).
 * - useSemVisita / useArquivosSemVisita: documentos sem visita por motivo e a lista de um
 *   motivo (ADR-034), com o mesmo polling dos totais.
 *
 * Nenhum dado pessoal vai a console/analytics (RN-M11-001). Erros (401/403/422)
 * são expostos como flags/estado para as telas tratarem (RF 1–6, Req 10 AC6).
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Evidencia } from '../services/consulta-types';
import { useServices } from '../contexts/ServicesContext';
import type { ContornoUfs } from '../components/mapa/projecao';
import { carregarContornoUfs } from '../services/contornoUfs';
import {
  AtualizarConfigInput,
  ConfigWhiteLabel,
  CriarReprocessamentoInput,
  CriarReprocessamentoResponse,
  CriterioEstimativa,
  DocumentosSemVisita,
  EstimativaReprocessamento,
  Mapa,
  MotivoSemVisita,
  PaginaArquivosSemVisita,
  Qualidade,
  ReprocessamentoResumoItem,
  ReprocessamentoStatus,
  SugestoesReprocessamento,
  Totais,
} from '../services/painel-types';

/** Intervalo de polling do painel (≤ 60s — RNF de atualização). */
export const PAINEL_POLL_MS = 60000;
/** Polling do status de reprocessamento enquanto em andamento (mais curto). */
export const REPROC_POLL_MS = 5000;

export interface EstadoTotais {
  readonly totais: Totais | undefined;
  readonly carregando: boolean;
  readonly erro: boolean;
}

/** GET /painel/totais — cards, com polling. */
/** ADR-038: `evidencia` restringe as visitas a um tipo (o filtro Evidência do painel). */
export function useTotais(evidencia?: Evidencia): EstadoTotais {
  const { painel } = useServices();
  const query = useQuery({
    queryKey: ['painel', 'totais', evidencia ?? 'todas'],
    queryFn: () => painel.totais(evidencia),
    refetchInterval: PAINEL_POLL_MS,
  });
  return { totais: query.data, carregando: query.isLoading, erro: query.isError };
}

export interface EstadoQualidade {
  readonly qualidade: Qualidade | undefined;
  readonly carregando: boolean;
  readonly erro: boolean;
}

/** GET /painel/qualidade — donut + ranking, com polling. */
/** ADR-038: `evidencia` separa a qualidade por tipo (as da planilha começam em alerta). */
export function useQualidade(evidencia?: Evidencia): EstadoQualidade {
  const { painel } = useServices();
  const query = useQuery({
    queryKey: ['painel', 'qualidade', evidencia ?? 'todas'],
    queryFn: () => painel.qualidade(evidencia),
    refetchInterval: PAINEL_POLL_MS,
  });
  return { qualidade: query.data, carregando: query.isLoading, erro: query.isError };
}

export interface EstadoMapa {
  readonly mapa: Mapa | undefined;
  readonly carregando: boolean;
  readonly erro: boolean;
}

/** GET /painel/mapa — distribuição por UF + pontos. Contrato req{—}: sem
 * parâmetros. Qualquer filtragem é client-side sobre os dados retornados. */
export function useMapa(): EstadoMapa {
  const { painel } = useServices();
  const query = useQuery({
    queryKey: ['painel', 'mapa'],
    queryFn: () => painel.mapa(),
  });
  return { mapa: query.data, carregando: query.isLoading, erro: query.isError };
}

export interface EstadoContornoUfs {
  readonly contorno: ContornoUfs | undefined;
  readonly carregando: boolean;
  readonly erro: boolean;
}

/** Contorno das UFs do mapa (ADR-032): asset estático em chunk próprio, carregado uma vez e mantido em cache.
 * Só a tela do mapa usa este hook — as demais telas não baixam o chunk (Req 3 AC3). */
export function useContornoUfs(): EstadoContornoUfs {
  const query = useQuery({
    queryKey: ['mapa', 'contorno'],
    queryFn: () => carregarContornoUfs(),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
  return { contorno: query.data, carregando: query.isLoading, erro: query.isError };
}

/** GET /config — white-label corrente. */
export function useConfig(): {
  config: ConfigWhiteLabel | undefined;
  carregando: boolean;
  erro: boolean;
} {
  const { painel } = useServices();
  const query = useQuery({
    queryKey: ['config'],
    queryFn: () => painel.obterConfig(),
  });
  return { config: query.data, carregando: query.isLoading, erro: query.isError };
}

/** PATCH /config — atualiza white-label (admin); invalida a config no sucesso. */
export function useAtualizarConfig(): {
  atualizar(input: AtualizarConfigInput): Promise<ConfigWhiteLabel>;
  enviando: boolean;
  erro: boolean;
} {
  const { painel } = useServices();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: AtualizarConfigInput) => painel.atualizarConfig(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['config'] });
    },
  });
  return {
    atualizar: (input) => mutation.mutateAsync(input),
    enviando: mutation.isPending,
    erro: mutation.isError,
  };
}

/** POST /reprocessamentos — dispara (admin). Devolve a resposta ao chamador. */
export function useCriarReprocessamento(): {
  criar(input: CriarReprocessamentoInput): Promise<CriarReprocessamentoResponse>;
  enviando: boolean;
  erro: boolean;
} {
  const { painel } = useServices();
  const mutation = useMutation({
    mutationFn: (input: CriarReprocessamentoInput) => painel.criarReprocessamento(input),
  });
  return {
    criar: (input) => mutation.mutateAsync(input),
    enviando: mutation.isPending,
    erro: mutation.isError,
  };
}

/** GET /reprocessamentos/estimativa — antes de confirmar (ADR-036); `null` = não consulta. */
export function useEstimativaReprocessamento(criterio: CriterioEstimativa | null): {
  estimativa: EstimativaReprocessamento | undefined;
  carregando: boolean;
  erro: boolean;
} {
  const { painel } = useServices();
  const query = useQuery({
    queryKey: ['reprocessamento-estimativa', criterio],
    queryFn: () => painel.estimarReprocessamento(criterio as CriterioEstimativa),
    enabled: criterio !== null,
  });
  return { estimativa: query.data, carregando: query.isLoading, erro: query.isError };
}

/** GET /reprocessamentos — os recentes (ADR-036), por página do cursor do contrato. */
export function useListaReprocessamentos(cursor: string | undefined): {
  pagina: { itens: readonly ReprocessamentoResumoItem[]; nextCursor: string | null } | undefined;
  carregando: boolean;
  erro: boolean;
} {
  const { painel } = useServices();
  const query = useQuery({
    queryKey: ['reprocessamentos', cursor ?? null],
    queryFn: () => painel.listarReprocessamentos(cursor),
  });
  return { pagina: query.data, carregando: query.isLoading, erro: query.isError };
}

/** GET /painel/sugestoes — só para quem pode reprocessar (rota do administrador); polling do painel. */
export function useSugestoesReprocessamento(habilitado: boolean): {
  sugestoes: SugestoesReprocessamento | undefined;
} {
  const { painel } = useServices();
  const query = useQuery({
    queryKey: ['painel', 'sugestoes'],
    queryFn: () => painel.sugestoes(),
    enabled: habilitado,
    refetchInterval: PAINEL_POLL_MS,
  });
  return { sugestoes: query.data };
}

/** True enquanto o reprocessamento está em andamento (enfileirado/em_execucao). */
export function reprocessamentoEmAndamento(status: Pick<ReprocessamentoStatus, 'status'> | undefined): boolean {
  return status?.status === 'enfileirado' || status?.status === 'em_execucao';
}

/** GET /reprocessamentos/{id} — status, com polling enquanto em andamento. */
export function useStatusReprocessamento(id: string | null): {
  status: ReprocessamentoStatus | undefined;
  carregando: boolean;
  erro: boolean;
} {
  const { painel } = useServices();
  const query = useQuery({
    queryKey: ['reprocessamento', id],
    queryFn: () => painel.statusReprocessamento(id as string),
    enabled: id !== null,
    refetchInterval: (q) => {
      const dados = q.state.data as ReprocessamentoStatus | undefined;
      return reprocessamentoEmAndamento(dados) ? REPROC_POLL_MS : false;
    },
  });
  return { status: query.data, carregando: query.isLoading, erro: query.isError };
}


export interface EstadoSemVisita {
  readonly conta: DocumentosSemVisita | undefined;
  readonly carregando: boolean;
  readonly erro: boolean;
}

/** GET /painel/sem-visita — o quadro dos documentos sem visita, com o polling do painel (Req 3 AC5). */
export function useSemVisita(): EstadoSemVisita {
  const { painel } = useServices();
  const query = useQuery({
    queryKey: ['painel', 'sem-visita'],
    queryFn: () => painel.semVisita(),
    refetchInterval: PAINEL_POLL_MS,
  });
  return { conta: query.data, carregando: query.isLoading, erro: query.isError };
}

/** GET /painel/sem-visita/arquivos — páginas da lista de um motivo (só quando aberto). */
export function useArquivosSemVisita(motivo: MotivoSemVisita | null) {
  const { painel } = useServices();
  return useInfiniteQuery<PaginaArquivosSemVisita>({
    queryKey: ['painel', 'sem-visita', 'arquivos', motivo],
    queryFn: ({ pageParam }) => painel.arquivosSemVisita(motivo as MotivoSemVisita, pageParam as string | undefined),
    initialPageParam: undefined,
    getNextPageParam: (ultima) => ultima.nextCursor ?? undefined,
    enabled: motivo !== null,
  });
}
